/**
 * Merch PLP behaviour — colour swatch selection on a product card.
 *
 * Clicking a swatch swaps the card's image, updates the colour name beneath it, and rewrites
 * the card link to that colour's ?variant=, so the choice survives the click through to the
 * PDP (merch-product.js seeds itself from that param).
 *
 * The swatch buttons sit outside the card's <a> so a tap can never navigate.
 *
 * Public API on `window.MerchPLP` (mirrors window.MerchPDP in merch-product.js):
 *
 *   Lifecycle
 *     hydrate(scope)        -> mount every un-mounted [data-merch-card] in scope
 *     destroyWithin(scope)  -> tear down the cards inside scope
 *     instances             -> Map<instanceId, MerchCard>
 *     get(instanceId)       -> one card instance
 *
 *   State
 *     getState(instanceId)  -> { instanceId, color, variantId }
 *     select(color, opts)   -> set a card's colour through the same path a click takes
 *
 *   Events (dispatched on document, bubbling) — the seam for Convert experiments
 *     merch:card:ready   detail { instanceId, state }
 *     merch:card:select  detail { instanceId, state, previous }
 *
 * detail.state and detail.previous are both getState() shapes, matching window.MerchPDP.
 *
 * The PDP has its own gallery and selection logic in assets/merch-product.js.
 */
(function () {
    'use strict';

    const SELECTOR = '[data-merch-card]';

    let instanceCounter = 0;

    class MerchCard {
        constructor(root) {
            this.root = root;
            this.initialized = false;

            this.swatches = Array.from(root.querySelectorAll('[data-merch-swatch]'));
            if (this.swatches.length === 0) return;

            this.instanceId = `merch-card-${++instanceCounter}`;
            this.controller = new AbortController();

            this.image = root.querySelector('[data-merch-card-image]');
            this.label = root.querySelector('[data-merch-color-name]');
            this.link = root.querySelector('[data-merch-card-link]');

            const active = this.swatches.find((s) => s.classList.contains('is-selected')) || this.swatches[0];
            this.color = active.dataset.color;

            this.bindEvents();

            this.initialized = true;
            this.emit('merch:card:ready', { state: this.getState() });
        }

        bindEvents() {
            const { signal } = this.controller;

            this.swatches.forEach((swatch) => {
                swatch.addEventListener('click', () => this.select(swatch.dataset.color), { signal });
            });
        }

        // See MerchPDPApp.isMounted() — a Convert variation that rewrites the grid leaves the
        // mount marker behind on a root whose children are all detached.
        isMounted() {
            return !!(this.swatches[0] && this.root.contains(this.swatches[0]));
        }

        destroy() {
            if (this.controller) this.controller.abort();
            this.initialized = false;
            delete this.root.dataset.merchCardInstance;
        }

        emit(name, detail) {
            document.dispatchEvent(new CustomEvent(name, {
                bubbles: true,
                detail: Object.assign({ instanceId: this.instanceId }, detail),
            }));
        }

        getState() {
            const swatch = this.swatchFor(this.color);
            // Number, to match the variantId merch-pdp-data.liquid emits unquoted — so a
            // Convert handler can compare PLP and PDP state without a type coercion trap.
            const variantId = swatch ? parseInt(swatch.dataset.variantId, 10) : NaN;
            return {
                instanceId: this.instanceId,
                color: this.color,
                variantId: Number.isNaN(variantId) ? null : variantId,
            };
        }

        swatchFor(color) {
            return this.swatches.find((s) => s.dataset.color === color) || null;
        }

        /**
         * The single selection path — every click, and every programmatic call from a Convert
         * variation, goes through here.
         */
        select(color, { silent = false } = {}) {
            const target = this.swatchFor(color);
            if (!target) return this.getState();

            const previous = this.getState();
            this.color = color;

            this.swatches.forEach((swatch) => {
                const isSelected = swatch === target;
                swatch.classList.toggle('is-selected', isSelected);
                swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            });

            const nextImage = target.dataset.image;
            if (this.image && nextImage) this.image.src = nextImage;
            if (this.label) this.label.textContent = color;

            // Carry the choice through to the PDP, which seeds itself from ?variant=.
            const variantId = target.dataset.variantId;
            if (this.link && this.link.dataset.baseUrl && variantId) {
                this.link.href = `${this.link.dataset.baseUrl}?variant=${variantId}`;
            }

            const state = this.getState();
            if (!silent) this.emit('merch:card:select', { state, previous });
            return state;
        }
    }

    const MerchPLPManager = {
        cards: new Map(),

        hydrate(scope = document) {
            const roots = scope.querySelectorAll ? Array.from(scope.querySelectorAll(SELECTOR)) : [];
            if (scope.matches && scope.matches(SELECTOR)) roots.unshift(scope);

            roots.forEach((root) => {
                const existingId = root.dataset.merchCardInstance;
                if (existingId) {
                    const existing = MerchPLPManager.cards.get(existingId);
                    if (existing && existing.isMounted()) return;
                    MerchPLPManager.destroyInstance(existingId);
                }

                const card = new MerchCard(root);
                if (!card.initialized) return;

                root.dataset.merchCardInstance = card.instanceId;
                MerchPLPManager.cards.set(card.instanceId, card);
            });
        },

        destroyWithin(scope = document) {
            const roots = scope.querySelectorAll ? Array.from(scope.querySelectorAll(SELECTOR)) : [];
            if (scope.matches && scope.matches(SELECTOR)) roots.unshift(scope);

            roots.forEach((root) => {
                const instanceId = root.dataset.merchCardInstance;
                if (instanceId) MerchPLPManager.destroyInstance(instanceId);
            });
        },

        destroyInstance(instanceId) {
            const card = MerchPLPManager.cards.get(instanceId);
            if (!card) return;
            card.destroy();
            MerchPLPManager.cards.delete(instanceId);
        },

        get(instanceId) {
            if (instanceId) return MerchPLPManager.cards.get(instanceId) || null;
            return MerchPLPManager.cards.values().next().value || null;
        },
    };

    function ready(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
            return;
        }
        callback();
    }

    ready(() => MerchPLPManager.hydrate(document));

    document.addEventListener('shopify:section:load', (event) => {
        if (event && event.target) MerchPLPManager.hydrate(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
        if (event && event.target) MerchPLPManager.destroyWithin(event.target);
    });

    const facade = window.MerchPLP || {};
    facade.hydrate = (scope) => MerchPLPManager.hydrate(scope || document);
    facade.destroyWithin = (scope) => MerchPLPManager.destroyWithin(scope || document);
    facade.get = (instanceId) => MerchPLPManager.get(instanceId);
    facade.getState = (instanceId) => {
        const card = MerchPLPManager.get(instanceId);
        return card ? card.getState() : null;
    };
    facade.select = (color, options) => {
        const card = MerchPLPManager.get(options && options.instanceId);
        return card ? card.select(color, options) : null;
    };
    facade.instances = MerchPLPManager.cards;
    window.MerchPLP = facade;
})();
